import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from 'app/api';

export const createQRData = createAsyncThunk(
  'qr/create',
  async ({ qrData, siteId }, { dispatch, rejectWithValue }) => {
    try {
      const res = await API.post('/CreateQRData', {
          Name: qrData.Name,
          SiteId: siteId,
          IntendedX: qrData.IntendedX ?? 0,
          IntendedY: qrData.IntendedY ?? 0,
          IntendedZ: qrData.IntendedZ ?? 0,
        });
      dispatch(getAllQRBySiteId({ siteId }));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Network error. Please try again.');
    }
  }
);

export const getAllQRBySiteId = createAsyncThunk(
  'qr/getAll',
  async ({ siteId }, { rejectWithValue }) => {
    try {
      const res = await API.get('/GetAllQRBySiteId', { params: { SiteId: siteId } });
      if (Array.isArray(res.data)) return res.data;
      if (Array.isArray(res.data?.data)) return res.data.data;
      throw new Error('Received unexpected data from the server.');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Network error. Please try again.');
    }
  }
);

export const deleteQRData = createAsyncThunk(
  'qr/delete',
  async ({ siteId, qrData }, { dispatch, rejectWithValue }) => {
    try {
      const res = await API.delete('/DeleteQRData', { params: { QRID: qrData.qrid } });
      dispatch(getAllQRBySiteId({ siteId }));
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Network error. Please try again.');
    }
  }
);

export const updateQRData = createAsyncThunk(
  'qr/update',
  async ({ siteId, qrData }, { dispatch, rejectWithValue }) => {
    try {
      const res = await API.put('/UpdateQRData', qrData, { params: { QRID: qrData.qrid } });
      dispatch(getAllQRBySiteId({ siteId }));
      return res.data;
    } catch (err) {
      // Undo the optimistic patch applied on pending.
      dispatch(getAllQRBySiteId({ siteId }));
      return rejectWithValue(err.response?.data?.message || 'Network error. Please try again.');
    }
  }
);

export const generateAndDownloadQRImage = createAsyncThunk(
  'qr/generate',
  async ({ siteId, qrData }, { rejectWithValue }) => {
    try {
      const res = await API.post(
        '/GenerateQRImage',
        { qrData: Array.isArray(qrData) ? qrData : [qrData] },
        { params: { SiteId: siteId }, responseType: 'blob' }
      );

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'QRCodes.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Network error. Please try again.');
    }
  }
);

const qrSlice = createSlice({
  name: 'QRData',
  initialState: {
    QRData: {},
    QRDataList: [],
    uploadedData: {},
    lastDownloadedQRData: null,
    getFilesError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllQRBySiteId.fulfilled, (state, action) => {
        state.QRDataList = action.payload;
      })
      .addCase(getAllQRBySiteId.rejected, (state, action) => {
        state.getFilesError = action.payload;
      })
      .addCase(updateQRData.pending, (state, action) => {
        // Show the edit (name, coordinates, printed) at once; the refetch on
        // fulfilled/rejected reconciles with the server.
        const { qrData } = action.meta.arg;
        const idx = state.QRDataList.findIndex((qr) => qr.qrid === qrData.qrid);
        if (idx !== -1) {
          state.QRDataList[idx] = { ...state.QRDataList[idx], ...qrData };
        }
      })
      .addCase(updateQRData.fulfilled, (state, action) => {
        state.uploadedData = action.payload;
      })
      .addCase(generateAndDownloadQRImage.fulfilled, (state, action) => {
        state.lastDownloadedQRData = action.payload;
      });
  },
});

export default qrSlice.reducer;
